import { readCcpaConsent } from 'common/cookies/CookieUtils';
import { readRawCcpaConsent, writeCcpaConsent } from 'src/shared/ccpa/ccpaConsentStorage';
import { create } from 'zustand';
import { createSelectors } from './create-selectors';

export interface ConsentPreferences {
  advertising: boolean;
}

type ConsentStatus = 'pending' | 'accepted_all' | 'declined_all' | 'customized';

interface State {
  consentPreferences: ConsentPreferences;
  consentStatus: ConsentStatus;
  isConsentBannerShowing: boolean;
  isCustomizeModalShowing: boolean;
  isCaliforniaVisitor: boolean;
}

interface Actions {
  acceptAll: () => void;
  declineAll: () => void;
  saveCustomPreferences: (prefs: ConsentPreferences) => void;
  showConsentBanner: () => void;
  hideConsentBanner: () => void;
  showCustomizeModal: () => void;
  hideCustomizeModal: () => void;
}

const DEFAULT_PREFERENCES: ConsentPreferences = { advertising: false };

function readConsentFromStorage(): { status: ConsentStatus; preferences: ConsentPreferences } {
  const advertising = readCcpaConsent(readRawCcpaConsent());
  if (advertising === null) {
    return { status: 'pending', preferences: DEFAULT_PREFERENCES };
  }
  // TODO: With a single category (advertising), 'customized' is unreachable here since
  // advertising is always true or false. When a second category is added, derive 'customized'
  // as "some but not all categories enabled" instead of this ternary.
  return { status: advertising ? 'accepted_all' : 'declined_all', preferences: { advertising } };
}

const isCA = typeof window !== 'undefined' && window.isCaliforniaVisitor === 'true';
const ccpaEnabled = typeof window !== 'undefined' && window.ccpaConsentEnabled === 'true';
const initial = readConsentFromStorage();

const INITIAL_STATE: State = {
  consentPreferences: initial.preferences,
  consentStatus: initial.status,
  isConsentBannerShowing: ccpaEnabled && isCA && initial.status === 'pending',
  isCustomizeModalShowing: false,
  isCaliforniaVisitor: ccpaEnabled && isCA,
};

export const useCcpaConsentStore = create<State & Actions>((set) => ({
  ...INITIAL_STATE,
  acceptAll: () => {
    const prefs: ConsentPreferences = { advertising: true };
    writeCcpaConsent(prefs);
    set({
      consentPreferences: prefs,
      consentStatus: 'accepted_all',
      isConsentBannerShowing: false,
      isCustomizeModalShowing: false,
    });
  },
  declineAll: () => {
    const prefs: ConsentPreferences = { advertising: false };
    writeCcpaConsent(prefs);
    set({
      consentPreferences: prefs,
      consentStatus: 'declined_all',
      isConsentBannerShowing: false,
      isCustomizeModalShowing: false,
    });
  },
  saveCustomPreferences: (prefs: ConsentPreferences) => {
    writeCcpaConsent(prefs);
    set({
      consentPreferences: prefs,
      consentStatus: 'customized',
      isConsentBannerShowing: false,
      isCustomizeModalShowing: false,
    });
  },
  showConsentBanner: () => set({ isConsentBannerShowing: true }),
  hideConsentBanner: () => set({ isConsentBannerShowing: false }),
  showCustomizeModal: () => set({ isCustomizeModalShowing: true, isConsentBannerShowing: false }),
  hideCustomizeModal: () => set({ isCustomizeModalShowing: false }),
}));

export const useCcpaConsentStoreSelectors = createSelectors(useCcpaConsentStore);
